import React from 'react';
import {useTransition} from 'react-spring';

import isFunction from 'lodash/isFunction';
import last from 'lodash/last';
import noop from 'lodash/noop';
import uniqueId from 'lodash/uniqueId';

import {useAutoFocus, useEvent, useHotkey} from '@edna/hooks';
import {events, hexToRgba} from '@edna/utils';

import {useTheme} from '../styles';
import {EEvent} from '../types';
import {easing} from '../utils';
import Alert, {TProps as TAlertProps} from './Alert';
import Confirm, {TProps as TConfirmProps} from './Confirm';
import Modal from './Modal';
import * as S from './style';

export * from './Modal';
export {Modal};

type TProps = {
  className?: string;
  containerId?: string;
  container?: React.FC<TContainerProps>;
  modals?: TItem[];
  setModals?: React.Dispatch<React.SetStateAction<TItem[]>>;
};

type TModal = React.ReactNode | ((closeModal: TEmptyFunction) => React.ReactNode);
type TModalProps = {
  autofocus?: boolean;
  disableOverlayClick?: boolean;
  containerId?: string;
};

export const MODALS_ID = 'modals';

type TBackgroundItemProps = {
  show: boolean;
  hasAnimation: boolean;
};

const BackgroundItem = React.memo<TBackgroundItemProps>(({show, hasAnimation}) => {
  const {
    animation,
    palette: {black},
  } = useTheme();

  const transition = useTransition(show, {
    from: {
      backgroundColor: hexToRgba(black, 0),
      backdropFilter: 'blur(0px)',
    },
    enter: {
      backgroundColor: hexToRgba(black, 0.47),
      backdropFilter: 'blur(3px)',
    },
    leave: {
      backgroundColor: hexToRgba(black, 0),
      backdropFilter: 'blur(0px)',
    },
    config: {
      easing,
      duration: hasAnimation ? animation.duration : 0,
    },
  });

  return transition((style, visible) => visible && <S.Background style={style} />);
});

type TItem = {
  id: string;
  modal: TModal;
  modalProps: TModalProps;
};

const originalOverflow = window.getComputedStyle(document.body).overflow;
const updateDocumentOverflow = (overflow: string) => {
  document.documentElement.style.overflow = overflow;
};

type TContainerProps = {
  modals: TItem[];
  onModalClose: (id: TItem['id']) => void;
};

const AnimatedContainer = React.memo<TContainerProps>(({modals, onModalClose}) => {
  const {animation} = useTheme();

  const lastId = last(modals)?.id;

  const autofocusProps = useAutoFocus();

  const transition = useTransition(modals, {
    enter: {transform: 'scale(1)'},
    leave: {transform: 'scale(0)'},
    config: {
      easing,
      duration: animation.duration,
    },
  });

  return transition((style, {id, modal, modalProps}, {phase}, key) => {
    const closeModal = () => onModalClose(id);

    const showBackground = lastId === id;

    const hasAnimation =
      (modals.length === 1 && phase === 'mount' && key === 0) || modals.length < 1;

    const autofocus = modalProps.autofocus ? autofocusProps : {};

    return (
      <>
        <BackgroundItem show={showBackground} hasAnimation={hasAnimation} />
        <S.Background
          {...edna.renderTestId('modal-background')}
          onMouseDown={modalProps.disableOverlayClick ? noop : closeModal}
        >
          <S.ModalWrapper style={style} {...edna.renderTestId('modal-wrapper')} {...autofocus}>
            {isFunction(modal) ? modal(closeModal) : modal}
          </S.ModalWrapper>
        </S.Background>
      </>
    );
  });
});

const PrimitiveContainer = React.memo<TContainerProps>(({modals, onModalClose}) => {
  return (
    <>
      {modals.map(({modal, id}) => {
        return <>{isFunction(modal) ? modal(() => onModalClose(id)) : modal}</>;
      })}
    </>
  );
});

const Modals = React.memo<TProps>(
  ({className, containerId = MODALS_ID, container: Container = AnimatedContainer, ...props}) => {
    const state = React.useState<TItem[]>([]);
    const modals = props.modals ?? state[0];
    const setModals = props.setModals ?? state[1];

    const addModal = React.useCallback((modal: TModal, modalProps: TModalProps) => {
      setModals((prevState) => [...prevState, {modal, modalProps, id: uniqueId()}]);
    }, []);

    const removeModal = React.useCallback(
      (id: string) => setModals((prevState) => prevState.filter((modal) => modal.id !== id)),
      [],
    );

    const handleRemove = React.useCallback(() => {
      if (modals.length > 0) {
        removeModal(last(modals)!.id);
      }
    }, [modals, removeModal]);

    useHotkey('Escape', handleRemove);

    React.useEffect(() => {
      updateDocumentOverflow(modals.length > 0 ? 'hidden' : originalOverflow);
    }, [modals.length]);

    const removeAllModals = React.useCallback(() => setModals([]), [setModals]);

    useEvent('popstate', removeAllModals, {target: window});

    React.useEffect(() => {
      const eventId = `${EEvent.MODAL}_${containerId}`;

      events.on(eventId, addModal);

      return () => {
        events.off(eventId, addModal);
      };
    }, [addModal]);

    React.useEffect(() => {
      const eventId = `${EEvent.REMOVE_ALL_MODALS}_${containerId}`;

      events.on(eventId, removeAllModals);

      return () => {
        events.off(eventId, removeAllModals);
      };
    }, [removeAllModals]);

    return (
      <div className={className} id={containerId}>
        <Container modals={modals} onModalClose={removeModal} />
      </div>
    );
  },
);

export const showModal = (
  item: TModal,
  props: TModalProps = {autofocus: true},
  containerId: string = MODALS_ID,
) => events.trigger(`${EEvent.MODAL}_${containerId}`, item, props);

export const hideAllModal = (containerId: string = MODALS_ID) =>
  events.trigger(`${EEvent.REMOVE_ALL_MODALS}_${containerId}`);

export const showConfirm = (item: Omit<TConfirmProps, 'closeModal'>) =>
  showModal((closeModal) => <Confirm {...item} closeModal={closeModal} />);

export const showAlert = (item: Omit<TAlertProps, 'closeModal'>) =>
  showModal((closeModal) => <Alert {...item} closeModal={closeModal} />);

export {AnimatedContainer, PrimitiveContainer};

export default Modals;
